import { React, useRef, useState, useEffect, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Icon } from '@iconify/react';
import {
  Autocomplete,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Box,
  Grid,
  Typography,
  Button,
  Tooltip,
  TextField,
  InputAdornment,
  IconButton,
} from '@mui/material';
import {  useGridApiRef } from '@mui/x-data-grid-pro';
import { useParams } from 'react-router-dom';
import { useDispatch, useSelector } from '../../../../redux/store';
import Iconify from '../../../../reusable-components/iconify';
import {
  invoiceGLItemsForm,
  changeInvoiceField,
  getInvoiceLog,
  getInvoiceGls,
} from '../../../../redux/slices/invoicing';
import CustomDataGrid from '../../../../reusable-components/datagrid/CustomDataGrid';
import { INVOICE_GL_ITEM } from '../../../../reusable-components/datagrid/invoiceColumns';
import { useSnackbar } from '../../../../reusable-components/snackbar';
import { fCurrency } from '../../../../utils/formatNumber';
import usePrompt from '../../../../reusable-components/prompt/usePrompt';
import EditableCalcNumber from './EditableCalcNumber';
import { useIsInvoiceEditable } from './reusable/hooks/useInvoiceLocks';

GlItemsDataGrid.propTypes = {
  highlightedRows: PropTypes.bool,
  rows: PropTypes.array,
  openGlCodeSummary: PropTypes.bool,
  setOpenGlCodeSummary: PropTypes.func,
  highlightItem: PropTypes.object,
  invoiceDataLoaded: PropTypes.bool,
};
export default function GlItemsDataGrid({
  highlightedRows,
  rows = [],
  openGlCodeSummary,
  setOpenGlCodeSummary,
  highlightItem,
  invoiceDataLoaded,
}) {
  const { id } = useParams();
  const dispatch = useDispatch();
  const {
    data: { invoiceData, invoiceGLItems = [], invoiceItemData },
  } = useSelector((state) => state.invoicing);
  const { categories: cats, manualCategories, subcategories: subcats, manualSubcategories } = invoiceData;

  const categories =
    invoiceItemData?.invoice?.sourceMethod === null ? cats : /*sourceMethod === "Manual"*/ manualCategories || [];
  const subcategories =
    invoiceItemData?.invoice?.sourceMethod === null ? subcats : /*sourceMethod === "Manual"*/ manualSubcategories || [];
  const facilities =
    invoiceItemData?.invoice?.sourceMethod === 'Manual' ? invoiceData.manualFacilities : invoiceData.facilities;
  const isLoading = useSelector((state) => state.invoicing.isLoading);

  const apiRef = useGridApiRef();
  const { isEditable: hasEditAccess } = useIsInvoiceEditable();
  const { enqueueSnackbar } = useSnackbar();
  const [saveGls, setSaveGls] = useState(false);
  const [missingRequiredFields, setMissingRequiredFields] = useState(false);
  const [cellModesModel, setCellModesModel] = useState({});
  const [unsavedData, setUnsavedData] = useState({});

  usePrompt(Object.values(unsavedData)?.length > 0);
  const [isSaving, setIsSaving] = useState(false);

  const updateUnsavedData = (row, updatedRow) => {
    setUnsavedData((prev) => ({
      ...prev,
      [row.glItemId > 0 ? row.glItemId : row.id]: updatedRow,
    }));
    apiRef.current.updateRows([updatedRow]);
  };

  const updateMissingFields = () => {
    const unselectedFields = Object.values(unsavedData)?.some(
      (row) => row.facilityId === 0 || row.agoraSubcategoryId === 0
    );
    setMissingRequiredFields(unselectedFields);
  };

  const handleCellModesModelChange = useCallback((newModel) => {
    setCellModesModel(newModel);
  }, []);

  const handleAddGlRowClick = () => {
    const nextRowIndex = apiRef?.current?.getRowsCount();

    const newRow = {
      agoraCategory: '',
      agoraCategoryId: 0,
      agoraSubcategory: '',
      agoraSubcategoryId: 0,
      amount: '',
      deleteFlag: false,
      memo: null,
      facility: '',
      facilityId: 0,
      glCode: '',
      glItemId: 0,
      id: nextRowIndex,
      invoiceId: id,
      referenceNumber: null,
    };
    apiRef?.current?.updateRows([newRow]);
  };
  const updateInvoiceAmount = async () => {
    if (invoiceGLItems.length > 0) {
      const newShipping = invoiceGLItems.reduce(
        (accumulator, currentValue) => parseFloat(accumulator) + parseFloat(currentValue.shipping),
        0
      );
      const total =
        rows
          ?.map((i) => Number(i.amount) + Number(i.shipping))
          ?.reduce((accumulator, current) => accumulator + current, 0) ?? 0;

      await dispatch(changeInvoiceField(id, { key: 'Total', value: total }));
      await dispatch(changeInvoiceField(id, { key: 'Shipping', value: newShipping }));
      if (total > 0) await dispatch(changeInvoiceField(id, { key: 'InvoiceType', value: 'Invoice' }));
      else if (total < 0) await dispatch(changeInvoiceField(id, { key: 'InvoiceType', value: 'CreditMemo' }));
    } else {
      await dispatch(changeInvoiceField(id, { key: 'Total', value: 0 }));
      await dispatch(changeInvoiceField(id, { key: 'Shipping', value: 0 }));
    }

    setSaveGls(false);
  };
  useEffect(() => {
    if (saveGls) updateInvoiceAmount();
  }, [saveGls, invoiceGLItems]);
  useEffect(() => {
    if (highlightItem?.form === 'glcodessummary') {
      setOpenGlCodeSummary(true);
      const itemId = rows.find((row) => row.glItemId === highlightItem.id)?.id;
      if (!itemId || itemId < 0) return;
      const rowIndex = apiRef.current.getRowIndexRelativeToVisibleRows(itemId);
      if (rowIndex) apiRef.current.scrollToIndexes({ rowIndex, colIndex: 0 });
    }
  }, [highlightItem]);

  const saveGlItems = async () => {
    const response = await dispatch(invoiceGLItemsForm({ invoiceGlItems: Object.values(unsavedData) }));

    enqueueSnackbar(response.message ? response.message : 'Error updating GL Codes.', {
      variant: response.success ? 'success' : 'error',
      persist: response.keepFormOpen,
    });
    if (response.success) {
      setUnsavedData({});
      await dispatch(getInvoiceLog(id));
      const glRes = await dispatch(getInvoiceGls(id));
      if (glRes === 'success') setSaveGls(true);
    }
  };
  const cancelEditGlItems = () => {
    const newRowsToDelete = Object.values(unsavedData)
      .filter((x) => x.glItemId === 0)
      ?.map((row) => ({
        ...row,
        _action: 'delete',
      }));
    apiRef?.current?.updateRows(newRowsToDelete);
    apiRef?.current?.updateRows(rows);
    setUnsavedData({});
  };
  const customToolbar = useCallback(
    () => (
      <>
        <Grid container direction="row" justifyContent="flex-start" alignItems="flex-end">
          <>
            <Button
              variant="contained"
              color="secondary"
              size="small"
              startIcon={<Icon icon="material-symbols:add" width="1rem" height="1rem" />}
              onClick={handleAddGlRowClick}
              disabled={!hasEditAccess}
            >
              Add Gl code
            </Button>
            {Object.values(unsavedData).length > 0 && !missingRequiredFields && (
              <Tooltip title={missingRequiredFields ? 'Missing required fields' : ''} placement="top" arrow>
                <Box>
                  <Button
                    variant="outlined"
                    size="small"
                    color={missingRequiredFields ? 'error' : 'secondary'}
                    disabled={!hasEditAccess}
                    loading={isSaving}
                    type="submit"
                    sx={{ ml: 1 }}
                    onClick={saveGlItems}
                  >
                    Save
                  </Button>
                </Box>
              </Tooltip>
            )}

            {Object.values(unsavedData).length > 0 && (
              <Button
                variant="outlined"
                size="small"
                color="inherit"
                loading={isSaving}
                type="submit"
                sx={{ ml: 1 }}
                onClick={cancelEditGlItems}
              >
                Cancel
              </Button>
            )}
          </>
        </Grid>
      </>
    ),
    [apiRef, unsavedData, isSaving, missingRequiredFields]
  );

  const getRowOptions = useCallback(
    (row) => {
      const existingGls = [...apiRef?.current?.getRowModels().values()]?.map((x) => ({
        glCode: x.glCode,
        facilityId: x.facilityId,
      }));
      const facilityCategories = categories?.filter((x) => x.facilityId === row.facilityId) || [];
      const facilitySubcategories = subcategories.filter((sub) => sub?.facilityId === row.facilityId);
      const subcategoriesOptions = (
        row.agoraCategoryId !== 0
          ? facilitySubcategories.filter((x) => x.categoryId === 0 || x.categoryId === row.agoraCategoryId)
          : facilitySubcategories
      )?.filter((sub) => {
        const isDifferentGlCode = row?.glCode !== sub.glCode;
        const isNotInExistingGlCodes = !existingGls?.some(
          (existing) => existing.glCode === sub.glCode && existing.facilityId === sub.facilityId
        );
        return sub.glCode !== null && (isDifferentGlCode ? isNotInExistingGlCodes : true);
      });
      const categoriesOptions = facilityCategories.filter((cat) => {
        const subcategoriesCat = subcategoriesOptions.filter(
          (x) => x.categoryId === cat.categoryId || x.categoryId === 0
        );
        if (row.agoraSubcategoryId === 0) return subcategoriesCat.length > 0;
        return subcategoriesCat.some((x) => x.subcategoryId === row.agoraSubcategoryId);
      });
      const nonDependantGls = facilitySubcategories.some((x) => x.categoryId === 0);
      return { categoriesOptions, facilityCategories, subcategoriesOptions, facilitySubcategories, nonDependantGls };
    },
    [categories, subcategories]
  );

  const actionButtons = (params) => {
    const { row } = params;
    return (
      <>
        {hasEditAccess && (
          <IconButton
            color="error"
            actionButton="Delete"
            onClick={() => {
              const rowToDelete = { ...row, deleteFlag: true, _action: 'delete' };
              setUnsavedData((prev) => {
                const updatedData = { ...prev };
                if (row.glItemId > 0) {
                  updatedData[row.glItemId] = rowToDelete;
                } else {
                  const rowId = Object.values(updatedData).find(
                    (x) =>
                      x.glItemId === 0 &&
                      x.agoraSubcategoryId === row.agoraSubcategoryId &&
                      x.glCode === row.glCode &&
                      row.amount === x.amount &&
                      row.shipping === x.shipping &&
                      row.referenceNumber === x.referenceNumber &&
                      row.memo === x.memo
                  )?.id;

                  if (rowId !== undefined) {
                    delete updatedData[rowId];
                  }
                }
                return updatedData;
              });
              apiRef?.current?.updateRows([rowToDelete]);
              const reorderedRows = Array.from(apiRef?.current?.getRowModels()?.values() || []).map((item, index) => ({
                ...item,
                id: index,
              }));
              apiRef?.current?.setRows(reorderedRows);
            }}
          >
            <Iconify icon="material-symbols:delete-outline" width={20} height={20} color="error" />
          </IconButton>
        )}
      </>
    );
  };

  const renderFacility = (params) => {
    const { row } = params;
    return hasEditAccess ? (
      <Autocomplete
        sx={{ fontSize: '12px', flexGrow: 1 }}
        options={facilities || []}
        onChange={(e, newValue) => {
          const updatedRow = {
            ...row,
            facilityId: newValue.value,
            facility: newValue.label,
            agoraCategory: '',
            agoraCategoryId: 0,
            agoraSubcategory: '',
            agoraSubcategoryId: 0,
            glCode: '',
          };
          updateUnsavedData(row, updatedRow);
        }}
        loading={!invoiceDataLoaded}
        disableClearable
        value={facilities?.find((x) => x.value === row?.facilityId) || null}
        renderInput={(params) => (
          <TextField
            autoComplete="off"
            {...params}
            InputProps={{
              ...params.InputProps,
              style: {
                fontSize: '0.8rem',
                fontWeight: 600,
                padding: 0,
                paddingRight: '25px',
              },
            }}
            variant="outlined"
          />
        )}
      />
    ) : (
      row?.facility ?? null
    );
  };

  const renderCategory = (params) => {
    const { row } = params;

    const { facilityCategories, categoriesOptions, nonDependantGls } = getRowOptions(row);
    return hasEditAccess && facilityCategories.length > 0 ? (
      <Autocomplete
        sx={{ fontSize: '12px', flexGrow: 1 }}
        getOptionLabel={(option) => option.category}
        getOptionValue={(option) => option.categoryId}
        options={categoriesOptions || []}
        onChange={(e, newValue) => {
          const updatedRow = {
            ...row,
            agoraCategory: newValue.category,
            agoraCategoryId: newValue.categoryId,
            ...(!nonDependantGls ? { agoraSubcategoryId: 0, agoraSubcategory: '', glCode: '' } : {}),
          };
          updateUnsavedData(row, updatedRow);
        }}
        loading={!invoiceDataLoaded}
        disableClearable
        value={categoriesOptions?.find((x) => x.categoryId === row?.agoraCategoryId) || null}
        renderInput={(params) => (
          <TextField
            {...params}
            autoComplete="off"
            InputProps={{
              ...params.InputProps,
              style: {
                fontSize: '0.8rem',
                fontWeight: 600,
                padding: 0,
                paddingRight: '25px',
              },
            }}
            variant="outlined"
          />
        )}
      />
    ) : (
      row?.agoraCategory ?? null
    );
  };
  const renderSubcategory = (params) => {
    const { row } = params;
    const { subcategoriesOptions } = getRowOptions(row);
    return hasEditAccess ? (
      <Autocomplete
        sx={{ fontSize: '12px', flexGrow: 1 }}
        options={subcategoriesOptions || []}
        getOptionLabel={(option) => `${option.subcategory} ${option.glCode}`}
        getOptionValue={(option) => option.subcategoryId}
        value={subcategoriesOptions?.find((x) => x.subcategoryId === row?.agoraSubcategoryId) ?? null}
        onChange={(event, newValue) => {
          const updatedRow = {
            ...row,
            agoraSubcategory: newValue.subcategory,
            agoraSubcategoryId: newValue.subcategoryId,
            glCode: newValue.glCode,
          };
          updateUnsavedData(row, updatedRow);
        }}
        loading={!invoiceDataLoaded}
        disableClearable
        renderInput={(params) => (
          <TextField
            autoComplete="off"
            {...params}
            InputProps={{
              ...params.InputProps,
              style: {
                fontSize: '0.8rem',
                fontWeight: 600,
                padding: 0,
                paddingRight: '25px',
              },
            }}
            variant="outlined"
          />
        )}
      />
    ) : (
      `${row?.agoraCategory} ${row?.glCode}`
    );
  };

  const renderEditableCalcNumber = (field, row, currency = false) => (
    <EditableCalcNumber
      type="text"
      field={field}
      row={row}
      currency={currency}
      hasEditAccess={hasEditAccess}
      updateUnsavedData={updateUnsavedData}
      fCurrency={fCurrency}
    />
  );

  const renderEditableNumber = (field, row, currency = false) =>
    hasEditAccess ? (
      <TextField
        autoComplete="off"
        type={'number'}
        disabled={!hasEditAccess}
        size="small"
        value={row[field]}
        onChange={(e) => {
          const updatedRow = { ...row, [field]: Number(e.target.value) };
          updateUnsavedData(row, updatedRow);
        }}
        InputProps={
          currency && {
            startAdornment: (
              <InputAdornment position="start">
                <Typography sx={{ fontSize: '0.8rem', fontWeight: 600, color: '#404042' }}>$</Typography>
              </InputAdornment>
            ),
          }
        }
        sx={{
          '& .MuiInputBase-input': {
            fontSize: '0.8rem',
            fontWeight: 600,
          },
        }}
      />
    ) : currency ? (
      fCurrency(row[field])
    ) : (
      row[field]
    );
  const renderEditableString = (field, row) =>
    hasEditAccess ? (
      <TextField
        autoComplete="off"
        type={String}
        disabled={!hasEditAccess}
        size="small"
        value={row[field]}
        onChange={(e) => {
          const updatedRow = { ...row, [field]: e.target.value };
          updateUnsavedData(row, updatedRow);
        }}
        sx={{
          '& .MuiInputBase-input': {
            fontSize: '0.8rem',
            fontWeight: 600,
          },
        }}
      />
    ) : (
      row[field]
    );
  const actions = {
    field: 'actions',
    headerName: 'Actions',
    disableReorder: true,
    width: 120,
    renderCell: (params) => actionButtons(params),
    id: 'actions',
    className: 'actions',
  };
  const facility = {
    field: hasEditAccess ? 'facilityId' : 'facility',
    headerName: 'Facility',
    minWidth: 100,
    width: 170,
    overflow: 'hidden',
    renderCell: renderFacility,
  };
  const glCategory = {
    field: 'agoraCategoryId',
    headerName: 'Category',
    minWidth: 100,
    flex: 1,
    overflow: 'hidden',
    renderCell: renderCategory,
  };
  const glSubcategory = {
    field: 'agoraSubcategoryId',
    headerName: 'Gl code',
    minWidth: 150,
    flex: 1,
    overflow: 'hidden',
    renderCell: renderSubcategory,
  };
  const amount = {
    field: 'amount',
    headerName: 'Amount',
    minWidth: 100,
    flex: 1,
    renderCell: (params) => renderEditableCalcNumber('amount', params.row, false),
    valueGetter: (params) => params.row.amount,
  };
  const shipping = {
    field: 'shipping',
    headerName: 'Shipping',
    minWidth: 100,
    flex: 1,
    renderCell: (params) => renderEditableNumber('shipping', params.row, true),
    valueGetter: (params) => params.row.shipping,
  };
  const totalAmount = {
    headerName: 'Total amount',
    type: 'customMoney',
    minWidth: 100,
    flex: 1,
    renderCell: (params) => fCurrency(parseFloat(params.row.amount) + parseFloat(params.row.shipping)),
    valueGetter: (params) => parseFloat(params.row.amount) + parseFloat(params.row.shipping),
  };
  const glMemo = {
    field: 'memo',
    headerName: 'Memo',
    minWidth: 100,
    renderCell: (params) => renderEditableString('memo', params.row),
  };
  const referenceNumber = {
    field: 'referenceNumber',
    headerName: 'Reference #',
    minWidth: 100,
    renderCell: (params) => renderEditableString('referenceNumber', params.row),
  };
  const columns = useMemo(() => {
    const commonColumns =
      invoiceItemData?.invoice?.sourceMethod !== 'Manual'
        ? [
            facility,
            glCategory,
            glSubcategory,
            amount,
            shipping,
            totalAmount,
            glMemo,
            referenceNumber,
            ...INVOICE_GL_ITEM,
            actions,
          ]
        : [facility, glCategory, glSubcategory, amount, glMemo, referenceNumber, ...INVOICE_GL_ITEM, actions];

    return commonColumns;
  }, [invoiceGLItems, invoiceData, invoiceItemData?.invoice?.sourceMethod, invoiceDataLoaded]);

  useEffect(() => {
    if (apiRef.current?.updateColumns) apiRef.current.updateColumns(columns);
  }, [invoiceGLItems, invoiceData, invoiceItemData?.invoice?.sourceMethod, invoiceDataLoaded, apiRef]);
  useEffect(() => {
    updateMissingFields();
  }, [unsavedData]);

  useEffect(() => {
    if (rows.length > 0 && invoiceGLItems.length === 0) {
      setUnsavedData((prev) => {
        const updatedData = { ...prev };
        rows.forEach((row) => {
          updatedData[row.id] = row;
        });
        return updatedData;
      });
    }
  }, [rows]);
  return (
    <>
      <Box sx={accordionBoxStyle}>
        <Accordion
          expanded={openGlCodeSummary}
          onChange={(e, newValue) => setOpenGlCodeSummary(newValue)}
          sx={accordionStyle}
        >
          <AccordionSummary
            sx={{
              color: 'info.main',
            }}
            expandIcon={<Iconify icon="ic:outline-expand-more" color="info" height="24px" width="24px" />}
          >
            <Typography sx={{ fontSize: '12px', fontWeight: 'bold' }}> GL Code summary &nbsp;</Typography>
          </AccordionSummary>
          <AccordionDetails>
            {rows && (
              <Box sx={{ maxHeight: '400px' }}>
                <CustomDataGrid
                  gridId="invoicing-invoice-overview-gl-items"
                  boxSX={{ width: '100%', height: '350px' }}
                  data={rows}
                  gridColumns={columns}
                  apiRef={apiRef}
                  isModal
                  CustomLeftToolbar={customToolbar}
                  // onCellClick={handleCellClick}
                  cellModesModel={cellModesModel}
                  onCellModesModelChange={handleCellModesModelChange}
                  sx={{
                    '& .MuiDataGrid-cell': {
                      marginY: '4px',
                      marginRight: '8px',
                      height: 38,

                      '&:focus-within': { outline: `none`, border: `none` },
                    },
                    '& .MuiDataGrid-cell.MuiDataGrid-cell--editing, & .MuiDataGrid-cell--editable': {
                      border: `solid 1px #404042`,
                      borderRadius: '6px',
                      fontSize: '0.8rem',
                      fontWeight: '600',
                      cursor: 'pointer',
                    },
                    '& .MuiDataGrid-columnHeader': {
                      marginRight: '8px',
                    },
                    '& .MuiBadge-badge': {
                      display: 'none',
                    },
                    '& .MuiDataGrid-row.row--highlight': {
                      backgroundColor: (theme) => {
                        if (theme.palette.mode === 'light') {
                          return 'rgba(143, 223, 130, 0.3)';
                        }
                        return 'rgba(143, 223, 130, 1)';
                      },
                    },
                    '& .MuiDataGrid-cell.cell--required': {
                      backgroundColor: 'rgba(255, 170, 170, 1)',
                    },

                    '& .MuiDataGrid-row.row--edited': {
                      backgroundColor: (theme) => {
                        if (theme.palette.mode === 'light') {
                          return 'rgba(255, 254, 176, 0.3)';
                        }
                        return 'rgba(255, 254, 176, 1)';
                      },
                    },
                  }}
                  getRowClassName={({ row }) => {
                    if (
                      highlightedRows?.includes(row?.id) ||
                      (highlightItem?.form === 'glcodessummary' && row.glItemId === highlightItem?.id)
                    )
                      return 'row--highlight';
                    return '';
                  }}
                  permanentHiddenColumns={['id', 'glItemId', 'deleteFlag']}
                  loading={isSaving}
                  isLoading={isLoading}
                />
              </Box>
            )}
          </AccordionDetails>
        </Accordion>
      </Box>
    </>
  );
}

const accordionBoxStyle = {
  borderRadius: '4px',
  boxShadow: '0 3px 6px 0 rgba(0, 0, 0, 0.16)',
  backgroundColor: '#fff',
  border: 'solid 1px #c1c9d0',
  width: '100%',
  mb: '20px',
};

const accordionStyle = {
  width: '100%',
  '&.Mui-expanded': {
    minHeight: '500px',
    maxHeight: '500px',
  },
};
